import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { eq } from 'tio-ui/utilities';
import {
  PhChatCircleDots,
  PhCheckCircle,
  PhArrowCounterClockwise,
  PhPaperPlaneTilt,
  PhUser,
  PhCaretDown,
  PhCaretRight,
} from 'ember-phosphor-icons';
import Drawer from 'tio-reimagined/components/drawer';
import type ComponentReviewService from 'tio-employee/services/component-review';
import type ComponentReviewThreadModel from 'tio-common/models/component-review-thread';
import type ComponentReviewCommentModel from 'tio-common/models/component-review-comment';

// ─── Helper: format relative time ──────────────────────────────────────────

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(isoString).toLocaleDateString();
}

// ─── Sub-component: Single Comment ─────────────────────────────────────────

interface CommentBubbleSignature {
  Args: {
    comment: ComponentReviewCommentModel;
  };
  Element: HTMLDivElement;
}

class CommentBubble extends Component<CommentBubbleSignature> {
  get timeAgo() {
    return formatRelativeTime(this.args.comment.createdAt);
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div class="flex gap-3 group" ...attributes>
      <div
        class="flex-none w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center"
      >
        <PhUser @size={{14}} class="text-violet-600 dark:text-violet-400" />
      </div>
      <div class="flex-1 min-w-0">
        <div class="flex items-baseline gap-2">
          <span class="text-sm font-medium text-gray-900">
            {{@comment.author.fullName}}
          </span>
          <span class="text-xs text-gray-400">
            {{this.timeAgo}}
          </span>
        </div>
        <div class="mt-1 text-sm text-gray-700 whitespace-pre-wrap">
          {{@comment.body}}
        </div>
      </div>
    </div>
  </template>
}

// ─── Sub-component: Thread Card ────────────────────────────────────────────

interface ThreadCardSignature {
  Args: {
    thread: ComponentReviewThreadModel;
    onResolve: (threadId: string) => void;
    onReopen: (threadId: string) => void;
    onAddComment: (threadId: string, body: string) => void;
  };
  Element: HTMLDivElement;
}

class ThreadCard extends Component<ThreadCardSignature> {
  @tracked isExpanded = this.args.thread.isOpen;
  @tracked newCommentText = '';

  get isClosed() {
    return this.args.thread.isResolved || this.args.thread.status === 'wontfix';
  }

  get threadAge() {
    return formatRelativeTime(this.args.thread.createdAt);
  }

  get commentCount() {
    return this.args.thread.commentCount;
  }

  get hasMultipleComments() {
    return this.commentCount !== 1;
  }

  toggleExpanded = () => {
    this.isExpanded = !this.isExpanded;
  };

  handleResolve = async () => {
    const id = this.args.thread.id;
    if (!id) return;
    await this.args.onResolve(id);
  };

  handleReopen = async () => {
    const id = this.args.thread.id;
    if (!id) return;
    await this.args.onReopen(id);
  };

  submitComment = (event: Event) => {
    event.preventDefault();
    const text = this.newCommentText.trim();
    const id = this.args.thread.id;
    if (!text || !id) return;
    this.args.onAddComment(id, text);
    this.newCommentText = '';
  };

  updateCommentText = (event: Event) => {
    this.newCommentText = (event.target as HTMLTextAreaElement).value;
  };

  get isSubmitDisabled() {
    return this.newCommentText.trim().length === 0;
  }

  handleKeyDown = (event: KeyboardEvent) => {
    // Cmd/Ctrl+Enter to submit
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.submitComment(event);
    }
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="border rounded-lg overflow-hidden
        {{if
          this.isClosed
          'border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20'
          'border-violet-200 dark:border-violet-800 bg-white'
        }}"
      ...attributes
    >
      {{! Thread header - clickable to expand/collapse }}
      <button
        type="button"
        class="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        {{on "click" this.toggleExpanded}}
      >
        {{#if this.isExpanded}}
          <PhCaretDown @size={{14}} class="text-gray-400 flex-none" />
        {{else}}
          <PhCaretRight @size={{14}} class="text-gray-400 flex-none" />
        {{/if}}

        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            {{#if this.isClosed}}
              <PhCheckCircle @size={{16}} @weight="fill" class="text-green-500 flex-none" />
              <span class="text-sm font-medium text-green-700 dark:text-green-400">
                {{if (eq @thread.status "wontfix") "Won't Fix" "Resolved"}}
              </span>
            {{else}}
              <PhChatCircleDots @size={{16}} class="text-violet-500 flex-none" />
              <span class="text-sm font-medium text-gray-900">
                Open
              </span>
            {{/if}}
            <span class="text-xs text-gray-400">
              ·
              {{this.commentCount}}
              comment{{if this.hasMultipleComments "s" ""}}
              ·
              {{this.threadAge}}
            </span>
          </div>
        </div>
      </button>

      {{! Thread body - comments + input }}
      {{#if this.isExpanded}}
        <div class="border-t border-gray-100">
          {{! Screenshot (if captured) }}
          {{#if @thread.screenshotUrl}}
            <div class="p-3 border-b border-gray-100">
              <img
                src={{@thread.screenshotUrl}}
                alt="Component screenshot"
                class="w-full rounded-lg border border-gray-200 shadow-sm"
              />
            </div>
          {{/if}}

          {{! Comments list }}
          <div class="p-4 space-y-4">
            {{#each @thread.comments as |comment|}}
              <CommentBubble @comment={{comment}} />
            {{/each}}
          </div>

          {{! Actions bar }}
          <div class="px-4 py-2 border-t border-gray-100 flex items-center gap-2">
            {{#if this.isClosed}}
              <button
                type="button"
                class="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/50 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
                {{on "click" this.handleReopen}}
              >
                <PhArrowCounterClockwise @size={{12}} />
                Reopen
              </button>
            {{else}}
              <button
                type="button"
                class="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/50 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                {{on "click" this.handleResolve}}
              >
                <PhCheckCircle @size={{12}} />
                Resolve
              </button>
            {{/if}}
          </div>

          {{! New comment input }}
          {{#unless this.isClosed}}
            <form
              class="p-3 border-t border-gray-100 bg-gray-50 dark:bg-gray-800/50"
              {{on "submit" this.submitComment}}
            >
              <textarea
                class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                rows="2"
                placeholder="Add a comment... (⌘+Enter to send)"
                value={{this.newCommentText}}
                {{on "input" this.updateCommentText}}
                {{on "keydown" this.handleKeyDown}}
              ></textarea>
              <div class="flex justify-end mt-2">
                <button
                  type="submit"
                  class="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  disabled={{this.isSubmitDisabled}}
                >
                  <PhPaperPlaneTilt @size={{12}} />
                  Send
                </button>
              </div>
            </form>
          {{/unless}}
        </div>
      {{/if}}
    </div>
  </template>
}

// ─── Main ReviewDrawer ─────────────────────────────────────────────────────

interface ReviewDrawerSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
    reviewId?: string;
    pagePath?: string;
    label?: string;
  };
  Element: HTMLDivElement;
}

/**
 * ReviewDrawer Component
 *
 * Slide-out drawer that shows all review threads for a specific component.
 * Allows creating new threads, adding comments, and resolving/reopening threads.
 *
 * Uses the same Drawer shell as DataSourceDocs for consistency.
 */
export default class ReviewDrawer extends Component<ReviewDrawerSignature> {
  @service declare componentReview: ComponentReviewService;

  @tracked newThreadComment = '';
  @tracked isCreatingThread = false;
  @tracked filter: 'all' | 'open' | 'resolved' = 'all';

  get threads(): ComponentReviewThreadModel[] {
    if (!this.args.reviewId) return [];
    const all = this.componentReview.getThreadsForComponent(this.args.reviewId, this.args.pagePath);

    switch (this.filter) {
      case 'open':
        return all.filter((t) => t.status === 'open');
      case 'resolved':
        return all.filter((t) => t.status === 'resolved');
      default:
        return all;
    }
  }

  get hasThreads() {
    return this.threads.length > 0;
  }

  get displayLabel() {
    return this.args.label ?? this.args.reviewId ?? 'Component';
  }

  get openCount() {
    if (!this.args.reviewId) return 0;
    return this.componentReview.getOpenThreadCount(this.args.reviewId, this.args.pagePath);
  }

  // ── New thread form ──────────────────────────────────────────────────

  startNewThread = () => {
    this.isCreatingThread = true;
  };

  cancelNewThread = () => {
    this.isCreatingThread = false;
    this.newThreadComment = '';
  };

  updateNewThreadComment = (event: Event) => {
    this.newThreadComment = (event.target as HTMLTextAreaElement).value;
  };

  submitNewThread = async (event: Event) => {
    event.preventDefault();
    const text = this.newThreadComment.trim();
    if (!text || !this.args.reviewId) return;

    // Capture screenshot from the component's DOM element (CSS.escape for safety)
    const escapedId =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(this.args.reviewId)
        : this.args.reviewId;
    const element = document.querySelector(`[data-review-id="${escapedId}"]`) as HTMLElement | null;
    const screenshot = element ? await this.componentReview.captureScreenshot(element) : undefined;

    await this.componentReview.createThread(this.args.reviewId, text, screenshot);
    this.newThreadComment = '';
    this.isCreatingThread = false;
  };

  handleNewThreadKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      this.submitNewThread(event);
    }
    if (event.key === 'Escape') {
      this.cancelNewThread();
    }
  };

  // ── Thread actions ────────────────────────────────────────────────────

  handleResolve = async (threadId: string) => {
    await this.componentReview.resolveThread(threadId);
  };

  handleReopen = async (threadId: string) => {
    await this.componentReview.reopenThread(threadId);
  };

  handleAddComment = async (threadId: string, body: string) => {
    await this.componentReview.addComment(threadId, body);
  };

  // ── Filter ────────────────────────────────────────────────────────────

  setFilterAll = () => {
    this.filter = 'all';
  };

  setFilterOpen = () => {
    this.filter = 'open';
  };

  setFilterResolved = () => {
    this.filter = 'resolved';
  };

  get isFilterAll() {
    return this.filter === 'all';
  }

  get isFilterOpen() {
    return this.filter === 'open';
  }

  get isFilterResolved() {
    return this.filter === 'resolved';
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{@onClose}}
      @size="lg"
      @placement="right"
      @closeOnOutsideClick={{true}}
      @closeOnEscapeKey={{true}}
    >
      <:header>
        <div class="flex items-center gap-2">
          <PhChatCircleDots @size={{24}} class="text-violet-600 dark:text-violet-400" />
          <div>
            <h2 class="text-gray-900 font-semibold">
              Review:
              {{this.displayLabel}}
            </h2>
            <p class="text-xs text-gray-500 mt-0.5">
              {{@pagePath}}
              ·
              {{this.openCount}}
              open
            </p>
          </div>
        </div>
      </:header>

      <:body>
        {{! Filter tabs }}
        <div class="flex gap-1 mb-4 p-1 bg-gray-100 rounded-lg">
          <button
            type="button"
            class="flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors
              {{if
                this.isFilterAll
                'bg-white text-gray-900 shadow-sm'
                'text-gray-500 hover:text-gray-700'
              }}"
            {{on "click" this.setFilterAll}}
          >
            All
          </button>
          <button
            type="button"
            class="flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors
              {{if
                this.isFilterOpen
                'bg-white text-gray-900 shadow-sm'
                'text-gray-500 hover:text-gray-700'
              }}"
            {{on "click" this.setFilterOpen}}
          >
            Open
          </button>
          <button
            type="button"
            class="flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors
              {{if
                this.isFilterResolved
                'bg-white text-gray-900 shadow-sm'
                'text-gray-500 hover:text-gray-700'
              }}"
            {{on "click" this.setFilterResolved}}
          >
            Resolved
          </button>
        </div>

        {{! Thread list }}
        <div class="space-y-3">
          {{#each this.threads as |thread|}}
            <ThreadCard
              @thread={{thread}}
              @onResolve={{this.handleResolve}}
              @onReopen={{this.handleReopen}}
              @onAddComment={{this.handleAddComment}}
            />
          {{/each}}

          {{#unless this.hasThreads}}
            <div class="text-center py-8">
              <PhChatCircleDots
                @size={{48}}
                class="mx-auto text-gray-300 dark:text-gray-600 mb-3"
              />
              <p class="text-sm text-gray-500">
                No threads yet for this component.
              </p>
              <p class="text-xs text-gray-400 mt-1">
                Start one below to discuss this section.
              </p>
            </div>
          {{/unless}}
        </div>

        {{! New thread form }}
        {{#if this.isCreatingThread}}
          <form
            class="mt-4 p-4 border-2 border-dashed border-violet-300 dark:border-violet-700 rounded-lg bg-violet-50/50 dark:bg-violet-950/20"
            {{on "submit" this.submitNewThread}}
          >
            <label class="block text-sm font-medium text-gray-700 mb-2">
              New Review Thread
            </label>
            <textarea
              class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
              rows="3"
              placeholder="What needs to be discussed about this section? (⌘+Enter to create)"
              value={{this.newThreadComment}}
              {{on "input" this.updateNewThreadComment}}
              {{on "keydown" this.handleNewThreadKeyDown}}
            ></textarea>
            <div class="flex justify-end gap-2 mt-2">
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
                {{on "click" this.cancelNewThread}}
              >
                Cancel
              </button>
              <button
                type="submit"
                class="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
              >
                <PhChatCircleDots @size={{12}} />
                Create Thread
              </button>
            </div>
          </form>
        {{/if}}
      </:body>

      <:footer>
        <div class="flex items-center justify-between">
          <span class="text-xs text-gray-400">
            Threads persist across sessions · Non-production only
          </span>
          {{#unless this.isCreatingThread}}
            <button
              type="button"
              class="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors"
              {{on "click" this.startNewThread}}
            >
              <PhChatCircleDots @size={{16}} />
              New Thread
            </button>
          {{/unless}}
        </div>
      </:footer>
    </Drawer>
  </template>
}
